import { useCallback, useEffect, useRef } from "react";
import type { Deck, Page } from "@deckloom/core/schema";

/** the keys under which a page (and its layout) names a media part, wherever they sit: pictures, posters, fills, bullets, fallbacks */
const MEDIA_KEYS = new Set(["mediaId", "poster", "fallbackMediaId"]);

/** The images a page draws, its layout's included (videos and sounds are left to their players). */
export function pageImages(deck: Deck, page: Page): string[] {
  const ids = new Set<string>();
  const walk = (v: unknown): void => {
    if (!v || typeof v !== "object") return;
    if (Array.isArray(v)) { for (const x of v) walk(x); return; }
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (MEDIA_KEYS.has(k) && typeof x === "string") ids.add(x);
      else walk(x);
    }
  };
  walk(page);
  if (page.layoutId) walk(deck.layouts[page.layoutId]);
  return [...ids].filter((id) => deck.media[id]?.kind === "image");
}

/** how long a page change waits for its pictures to decode before showing whatever is there */
const READY_CAP_MS = 400;

/**
 * The browser's cache warmed with the pictures of the pages about to show (`ahead` after the
 * current one, `behind` before it), so a slide comes up whole instead of its pictures popping in
 * one by one after the rest. Media is served immutable, so a warmed picture is never fetched again.
 * Returns `ready(page)`: settled once the page's pictures are decoded (asked for now if they were not),
 * or after a short cap, so the stage can hold the previous slide until the new one can be drawn whole.
 */
export function usePreloadImages(deck: Deck, pageIndex: number, mediaUrl: (mediaId: string) => string, ahead = 2, behind = 1): (page: Page) => Promise<void> {
  // what was asked for already (per deck): each picture's decode, and the Image objects kept alive until they are done
  const done = useRef<{ deck: Deck; loads: Map<string, Promise<void>>; live: Set<HTMLImageElement> } | null>(null);
  const state = () => {
    if (!done.current || done.current.deck !== deck) done.current = { deck, loads: new Map(), live: new Set() };
    return done.current;
  };
  const load = (id: string): Promise<void> => {
    const { loads, live } = state();
    const had = loads.get(id);
    if (had) return had;
    const img = new Image();
    live.add(img);
    img.src = mediaUrl(id);
    // decode() settles once the bitmap is ready (an error, a format the browser will not decode here, settles too)
    const p = (typeof img.decode === "function" ? img.decode() : new Promise<void>((r) => { img.onload = img.onerror = () => r(); })).catch(() => undefined).then(() => { live.delete(img); });
    loads.set(id, p);
    return p;
  };
  useEffect(() => {
    for (let i = pageIndex - behind; i <= pageIndex + ahead; i++) {
      const page = deck.pages[i];
      if (page) for (const id of pageImages(deck, page)) load(id);
    }
  }, [deck, pageIndex, mediaUrl, ahead, behind]); // eslint-disable-line react-hooks/exhaustive-deps
  return useCallback((page: Page) => {
    const all = Promise.all(pageImages(deck, page).map(load)).then(() => undefined);
    return Promise.race([all, new Promise<void>((r) => setTimeout(r, READY_CAP_MS))]);
  }, [deck, mediaUrl]); // eslint-disable-line react-hooks/exhaustive-deps
}
